import { Box, Heading, Text } from '@wix/design-system';
import css from '../SelfHostedModal.scss';
import React from 'react';
import { useSelfHostedContext } from '../SelfHostedModalContextProvider';
import { SelfHostedModalLayout } from '../SelfHostedModalLayout';
import { DeploymentOption } from '../components/DeploymentOption';
import { DataHooks } from '../../../../../constants/dataHooks';
import { useTemplatesData } from '../../../../../hooks/useTemplatesData';
import { DeploymentHostType } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { useTranslation } from '@wix/wix-i18n-config';
import { useDeploymentOptions } from '../useDeploymentOptions';
import { useTemplatesPageBi } from '../../../../../hooks/useTemplatesPageBi';
import type { SelfHostedModalBiParams } from '../types';

const biParams: SelfHostedModalBiParams = {
  stepName: 'deployment-process-step',
  mainCTAName: 'continue',
  secondaryCTAName: '',
};

export const DeploymentProcessStep = () => {
  const deploymentProviders = useDeploymentOptions();
  const { t } = useTranslation();
  const {
    formData: { selectedDeploymentProvider },
    updateSelectedDeploymentProvider,
    nextStep,
  } = useSelfHostedContext();
  const isContinueDisabled = !selectedDeploymentProvider;
  const { currentTemplate: template } = useTemplatesData();

  const { reportProviderSelected } = useTemplatesPageBi();

  const onDeploymentProviderSelect = (id: DeploymentHostType) => {
    reportProviderSelected(template.id, id);
    updateSelectedDeploymentProvider(id);
  };

  return (
    <SelfHostedModalLayout
      className={css.deployProcessStep}
      biParams={biParams}
      primaryButtonText={t(
        'app.template-page.modal.self-hosted.step1.primary-action',
      )}
      primaryButtonProps={{
        disabled: isContinueDisabled,
      }}
      primaryButtonTooltipProps={{
        content: isContinueDisabled
          ? t('app.template-page.modal.self-hosted.step1.continue.disabled')
          : null,
      }}
      primaryButtonOnClick={nextStep}
    >
      <Heading size="small" dataHook={DataHooks.DeploymentProcessTitle}>
        {t('app.template-page.modal.self-hosted.step1.title')}
      </Heading>
      <Text size="medium" weight="thin">
        {t('app.template-page.modal.self-hosted.step1.sub-title')}
      </Text>
      <Box className={css.options} gap="8px" direction="vertical">
        {[
          ...(template.deploymentHosts || []),
          {
            deploymentHostType: DeploymentHostType.UNKNOWN_DEPLOYMENT_HOST_TYPE,
          },
        ].map((deploymentHost) => {
          const deploymentStep =
            deploymentProviders[deploymentHost.deploymentHostType];
          return (
            <DeploymentOption
              key={deploymentStep.id}
              {...deploymentStep}
              selected={selectedDeploymentProvider}
              setSelected={onDeploymentProviderSelect}
            />
          );
        })}
      </Box>
    </SelfHostedModalLayout>
  );
};
